import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, type UserRole } from "@/lib/supabase";

type AuthMode = "login" | "signup";
type InputMethod = "email" | "phone";

const roles: { value: UserRole; label: string; icon: string; desc: string; color: string }[] = [
  { value: "student", label: "Student", icon: "ri-user-3-line", desc: "I want to learn & find tutors", color: "border-amber-400 bg-amber-50 text-amber-700" },
  { value: "teacher", label: "Teacher", icon: "ri-book-open-line", desc: "I want to teach & mentor", color: "border-emerald-400 bg-emerald-50 text-emerald-700" },
  { value: "parent", label: "Parent", icon: "ri-parent-line", desc: "I support my child's journey", color: "border-rose-400 bg-rose-50 text-rose-700" },
];

export default function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>("login");
  const [inputMethod, setInputMethod] = useState<InputMethod>("email");
  const [selectedRole, setSelectedRole] = useState<UserRole>("student");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (mode === "signup") {
        const identifier = inputMethod === "email" ? { email } : { phone };
        const { data, error: signUpError } = await supabase.auth.signUp({
          ...identifier,
          password,
          options: { data: { full_name: fullName, role: selectedRole } },
        });
        if (signUpError) throw signUpError;
        if (data.user) {
          await supabase.from("profiles").upsert({
            id: data.user.id,
            full_name: fullName,
            role: selectedRole,
            phone: inputMethod === "phone" ? phone : null,
            score: 0,
            weekly_streak: 0,
          });
          setSuccess("Account created! Please check your email to confirm, then log in.");
        }
      } else {
        const identifier = inputMethod === "email"
          ? { email, password }
          : { phone, password };
        const { error: signInError } = await supabase.auth.signInWithPassword(identifier);
        if (signInError) throw signInError;
        navigate("/");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/bcef6f6bba6454c7d517eb6d812ca4ce.jpg"
          alt="Library"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/80 via-stone-900/70 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-500">
              <i className="ri-graduation-cap-fill text-white text-lg"></i>
            </div>
            <div>
              <span className="font-bold text-lg text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Crown SAT</span>
              <span className="block text-xs text-amber-300 tracking-widest uppercase">&amp; University</span>
            </div>
          </Link>
          <div>
            <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your journey to the top starts here.
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Join thousands of students, teachers, and parents building brighter futures together — and growing virtual farms along the way!
            </p>
            <div className="flex flex-col gap-3">
              {[
                { icon: "ri-robot-2-line", text: "AI-powered matching with tutors & mentors" },
                { icon: "ri-seedling-line", text: "Earn score points & grow your virtual farm" },
                { icon: "ri-heart-line", text: "Help village students through donations" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500/30 flex-shrink-0">
                    <i className={`${item.icon} text-amber-300 text-sm`}></i>
                  </div>
                  <span className="text-white/80 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-white/30 text-xs">www.CrownSATandUniversity.com</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500">
              <i className="ri-graduation-cap-fill text-white"></i>
            </div>
            <span className="font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Crown SAT &amp; University</span>
          </Link>

          {/* Mode toggle */}
          <div className="flex bg-stone-100 rounded-full p-1 mb-8">
            {(["login", "signup"] as AuthMode[]).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); setSuccess(""); }}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  mode === m ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            {mode === "login" ? "Welcome back!" : "Join the community"}
          </h1>
          <p className="text-gray-400 text-sm mb-6">
            {mode === "login" ? "Sign in to your Crown SAT account." : "Create your free account in seconds."}
          </p>

          {/* Role selection (signup only) */}
          {mode === "signup" && (
            <div className="mb-6">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">I am a...</p>
              <div className="grid grid-cols-3 gap-2">
                {roles.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setSelectedRole(r.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedRole === r.value ? r.color + " border-2" : "border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200"
                    }`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${r.icon} text-lg`}></i>
                    </div>
                    <span className="text-xs font-semibold">{r.label}</span>
                    <span className="text-xs text-center leading-tight opacity-70 hidden sm:block">{r.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input method toggle */}
          <div className="flex bg-stone-100 rounded-full p-1 mb-5">
            {(["email", "phone"] as InputMethod[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setInputMethod(m)}
                className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  inputMethod === m ? "bg-white text-gray-900" : "text-gray-500"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={m === "email" ? "ri-mail-line" : "ri-phone-line"}></i>
                </div>
                {m === "email" ? "Email" : "Phone"}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            )}

            {inputMethod === "email" ? (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                </div>
                <p className="text-red-600 text-xs leading-relaxed">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>
                </div>
                <p className="text-emerald-700 text-xs leading-relaxed">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {mode === "login" ? "Signing in..." : "Creating account..."}
                </span>
              ) : (
                mode === "login" ? "Sign In" : "Create Account"
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); setSuccess(""); }}
              className="text-amber-600 font-semibold hover:underline cursor-pointer"
            >
              {mode === "login" ? "Sign up free" : "Sign in"}
            </button>
          </p>

          <p className="text-center text-xs text-gray-300 mt-4">
            By continuing, you agree to our{" "}
            <a href="#" className="hover:text-gray-500 transition-colors">Terms of Service</a>
            {" "}&amp;{" "}
            <a href="#" className="hover:text-gray-500 transition-colors">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
